/**
 * The firm's self-assessment worklist (DEC-21; DEC-31 as corrected 2026-10-04): the default view of "Each control"
 * for the firm's IT leads. It lists every control with where it stands and opens the control page at the assistant,
 * the only way a rating reaches National IT (DEC-12). It saves nothing.
 *
 * The address carries the work pill (?state=) and the catalogue filters (?wave=, ?place=, ?area=, ?priority=, ?q=),
 * so Home and the dashboard can open a slice directly. The old /self-assessment address redirects here.
 * The unfiltered list still leads with returned questions and unsent ratings. A wave or other catalogue filter
 * lists not yet in place first (placeRank), which does not change the dashboard counts.
 */
import { areaName, BTI_WAVE2_DUE, CDW_GAP_STATUS, CONTROL_SUMMARIES, placeRank, resultLabel, RISK_IMPACT, RISK_LABEL, RISK_ORDER, wave2Band, wave2Sentence, workRank, workState, type WorkState } from '@grc/shared/vocabulary';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { formatDate, RiskChip } from '../components';
import type { FirmControlRow, FirmControls } from '../types';

/** The state rule and order are shared with the API (packages/shared/src/worklist.ts), so counts and list agree. */
export { workState, type WorkState };

const STATE: Record<WorkState, { label: string; tone: string; action: string; short: string }> = {
  returned: { label: 'Returned with questions', tone: 'attention', action: "Answer National IT's questions", short: 'Answer' },
  not_rated: { label: 'Not rated', tone: 'neutral', action: 'Rate with the assistant', short: 'Rate' },
  draft: { label: 'Not sent to National IT', tone: 'attention', action: 'Send with the assistant', short: 'Send' },
  awaiting: { label: 'Awaiting review', tone: 'neutral', action: 'Open what was sent', short: 'Open' },
  reviewed: { label: 'Reviewed by National IT', tone: 'reviewed', action: 'Update with the assistant', short: 'Update' },
  cdw: { label: 'Assessed by CDW', tone: 'neutral', action: 'Update with the assistant', short: 'Update' },
};

/** The filters, in the order an IT lead works through them; "all" lists every control. */
type Filter = 'returned' | 'not_rated' | 'draft' | 'awaiting' | 'all';
const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'returned', label: 'Returned' },
  { id: 'not_rated', label: 'Not rated' },
  { id: 'draft', label: 'Not sent' },
  { id: 'awaiting', label: 'Awaiting review' },
  { id: 'all', label: 'All' },
];
const EMPTY: Record<Exclude<Filter, 'all'>, string> = {
  returned: 'Nothing has been returned with questions.',
  not_rated: 'Every control has a rating.',
  draft: 'Every rating the firm saved has been sent.',
  awaiting: 'Nothing is awaiting review.',
};
/** Returned questions and unsent drafts: what the IT lead should do before anything else. */
const FIRST = 'Do these first';
/** Drafts shown before the rest are folded away, so "Do these first" stays about one phone screen. */
const FIRST_DRAFTS = 5;

const isFilter = (v: string | null): v is Filter => FILTERS.some((f) => f.id === v);

const priority = (c: FirmControlRow) => RISK_ORDER[RISK_IMPACT[c.controlId]?.level ?? 'medium'];

export type WaveFilter = 'all' | '1' | '2' | 'none';
export type PlaceFilter = 'all' | 'gap' | 'partial' | 'full' | 'none';
export type PriorityFilter = 'all' | 'critical' | 'high' | 'medium';

export interface Catalog {
  wave: WaveFilter;
  place: PlaceFilter;
  area: string;
  priority: PriorityFilter;
  q: string;
}

const asWave = (v: string | null): WaveFilter => (v === '1' || v === '2' || v === 'none' ? v : 'all');
const asPlace = (v: string | null): PlaceFilter => (v === 'gap' || v === 'partial' || v === 'full' || v === 'none' ? v : 'all');
const asPriority = (v: string | null): PriorityFilter => (v === 'critical' || v === 'high' || v === 'medium' ? v : 'all');

export function readCatalog(params: URLSearchParams, areas: readonly string[]): Catalog {
  const area = params.get('area');
  return {
    wave: asWave(params.get('wave')),
    place: asPlace(params.get('place')),
    area: area && areas.includes(area) ? area : 'all',
    priority: asPriority(params.get('priority')),
    q: params.get('q')?.trim() ?? '',
  };
}

/** Copy of `base` with the given catalogue keys set. A blank value removes the key. Other keys stay. */
export function writeCatalog(base: URLSearchParams, patch: Partial<Catalog>): URLSearchParams {
  const next = new URLSearchParams(base);
  const put = (key: string, value: string | undefined, blank: string) => {
    if (value === undefined) return;
    const trimmed = value.trim();
    if (!trimmed || trimmed === blank) next.delete(key);
    else next.set(key, trimmed);
  };
  put('wave', patch.wave, 'all');
  put('place', patch.place, 'all');
  put('area', patch.area, 'all');
  put('priority', patch.priority, 'all');
  put('q', patch.q, '');
  return next;
}

export function catalogActive(f: Catalog): boolean {
  return f.wave !== 'all' || f.place !== 'all' || f.area !== 'all' || f.priority !== 'all' || f.q !== '';
}

/** Drop catalogue keys and a work pill the page does not understand. Returns null when the address is already usable. */
export function cleanSearch(params: URLSearchParams, areas: readonly string[]): URLSearchParams | null {
  const next = new URLSearchParams(params);
  const keep = (key: string, ok: (value: string) => boolean) => {
    const value = params.get(key);
    if (value !== null && !ok(value)) next.delete(key);
  };
  keep('wave', (v) => asWave(v) === v);
  keep('place', (v) => asPlace(v) === v);
  keep('area', (v) => areas.includes(v));
  keep('priority', (v) => asPriority(v) === v);
  keep('state', (v) => isFilter(v));
  const q = params.get('q');
  if (q !== null) {
    const trimmed = q.trim();
    if (!trimmed) next.delete('q');
    else if (trimmed !== q) next.set('q', trimmed);
  }
  return next.toString() === params.toString() ? null : next;
}

export function catalogMatch(c: Pick<FirmControlRow, 'btiWave' | 'currentStatus' | 'domainNumber' | 'controlId' | 'title'>, f: Catalog): boolean {
  if (f.wave === '1' || f.wave === '2') {
    if (String(c.btiWave) !== f.wave) return false;
  } else if (f.wave === 'none' && c.btiWave != null) return false;
  if (f.place === 'gap' && c.currentStatus !== 'gap') return false;
  if (f.place === 'partial' && c.currentStatus !== 'partial_gap') return false;
  if (f.place === 'full' && c.currentStatus !== 'no_gap') return false;
  if (f.place === 'none' && c.currentStatus != null) return false;
  if (f.area !== 'all' && c.domainNumber !== f.area) return false;
  if (f.priority !== 'all' && RISK_IMPACT[c.controlId]?.level !== f.priority) return false;
  if (f.q && !`${c.controlId} ${c.title} ${CONTROL_SUMMARIES[c.controlId] ?? ''}`.toLowerCase().includes(f.q.toLowerCase())) return false;
  return true;
}

const WAVE2_BUTTON = { gaps: 'Start with those', wave: 'Start with Wave 2', review: 'Review Wave 2' } as const;

/** True from the day after BTI's Wave 2 date. The due date itself still reads "is due". */
function wave2WasDue(today = new Date()): boolean {
  const end = new Date(`${BTI_WAVE2_DUE}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return today.getTime() >= end.getTime();
}

const liveSearch = () => new URLSearchParams(window.location.search);

/** The old address (Home, bookmarks): the worklist view of "Each control", keeping its filter. */
export function SelfAssessmentRedirect() {
  const { firmCode } = useParams();
  const [params] = useSearchParams();
  const state = params.get('state');
  const to = `/firm/${firmCode}/controls?view=worklist${state ? `&state=${encodeURIComponent(state)}` : ''}`;
  return <Navigate to={to} replace />;
}

export function Worklist({ d }: { d: FirmControls }) {
  const [params, setParams] = useSearchParams();
  const areas = [...new Set(d.controls.map((c) => c.domainNumber))];
  const catalog = readCatalog(params, areas);
  const narrowed = catalogActive(catalog);
  const raw = params.get('state');
  const filter: Filter = isFilter(raw) ? raw : 'all';
  const band = wave2Band(d.controls);
  const rows = d.controls.map((c) => ({ c, s: workState(c) }));
  const slice = rows.filter((r) => catalogMatch(r.c, catalog));
  const count = (id: WorkState | 'all') => (id === 'all' ? slice.length : slice.filter((r) => r.s === id).length);
  const shown = slice
    .filter((r) => filter === 'all' || r.s === filter)
    .sort((a, b) =>
      narrowed
        ? placeRank(a.c.currentStatus, a.c.surveyStatus) - placeRank(b.c.currentStatus, b.c.surveyStatus) || workRank(a.c) - workRank(b.c) || priority(a.c) - priority(b.c)
        : workRank(a.c) - workRank(b.c) || priority(a.c) - priority(b.c),
    );
  // Under "All", and only when the catalogue filters are off, what needs the firm's answer leads the page.
  const needs = !narrowed && filter === 'all' ? shown.filter((r) => r.s === 'returned' || r.s === 'draft') : [];
  const rest = needs.length ? shown.filter((r) => r.s !== 'returned' && r.s !== 'draft') : shown;
  const returnedCount = needs.filter((r) => r.s === 'returned').length;
  const needsShown = needs.slice(0, returnedCount + FIRST_DRAFTS);
  const needsMore = needs.slice(returnedCount + FIRST_DRAFTS);
  const areaIds = areas.filter((a) => rest.some((r) => r.c.domainNumber === a));
  const setCatalog = (patch: Partial<Catalog>, replace = false) => setParams(writeCatalog(liveSearch(), patch), replace ? { replace: true } : undefined);
  const startWave2 = () => {
    const next = writeCatalog(liveSearch(), { wave: '2', place: band.action === 'gaps' ? 'gap' : 'all', area: 'all', priority: 'all', q: '' });
    next.delete('state');
    setParams(next);
  };
  return (
    <>
      <p className="lede">
        {d.canUpdate
          ? 'Open a control to rate it with the control assistant. It checks your evidence with you and sends the rating to National IT for review.'
          : `${d.viewer === 'network' ? "This firm's" : "Your firm's"} IT leads rate each control with the control assistant. This list shows where each one stands.`}
      </p>
      {band.total > 0 && (
        <section className="work-wave" aria-label="BTI Wave 2">
          <p>{wave2Sentence(band, formatDate(BTI_WAVE2_DUE), wave2WasDue())}</p>
          <button type="button" className="pill work-wave-action" onClick={startWave2}>
            {WAVE2_BUTTON[band.action]}
          </button>
        </section>
      )}
      <div className="filters work-catalog" role="search" aria-label="Filter the list">
        <label>
          Area
          <select value={catalog.area} onChange={(e) => setCatalog({ area: e.target.value })}>
            <option value="all">All areas</option>
            {areas.map((a) => (
              <option key={a} value={a}>
                {areaName(a)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Priority
          <select value={catalog.priority} onChange={(e) => setCatalog({ priority: asPriority(e.target.value) })}>
            <option value="all">All</option>
            {(['critical', 'high', 'medium'] as const).map((l) => (
              <option key={l} value={l}>
                {RISK_LABEL[l]}
              </option>
            ))}
          </select>
        </label>
        <label>
          BTI wave
          <select value={catalog.wave} onChange={(e) => setCatalog({ wave: e.target.value as WaveFilter })}>
            <option value="all">All controls</option>
            <option value="1">Wave 1</option>
            <option value="2">Wave 2</option>
            <option value="none">Not in a wave</option>
          </select>
        </label>
        <label>
          Where it stands
          <select value={catalog.place} onChange={(e) => setCatalog({ place: e.target.value as PlaceFilter })}>
            <option value="all">All</option>
            <option value="gap">Not yet in place</option>
            <option value="partial">Partly in place</option>
            <option value="full">Fully in place</option>
            <option value="none">Not rated</option>
          </select>
        </label>
        <label>
          Search
          <input type="search" value={catalog.q} onChange={(e) => setCatalog({ q: e.target.value }, true)} placeholder="Word or number" />
        </label>
        {narrowed && (
          <button type="button" className="link" onClick={() => setCatalog({ wave: 'all', place: 'all', area: 'all', priority: 'all', q: '' })}>
            Clear filters
          </button>
        )}
      </div>
      <nav className="work-filters" aria-label="Show controls">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={f.id === filter ? 'pill work-filter selected' : 'pill work-filter'}
            aria-pressed={f.id === filter}
            onClick={() => {
              const next = liveSearch();
              if (f.id === 'all') next.delete('state');
              else next.set('state', f.id);
              setParams(next);
            }}
          >
            {f.label} <span className="work-count">{count(f.id)}</span>
          </button>
        ))}
      </nav>
      {shown.length === 0 ? (
        filter === 'all' && !narrowed ? (
          <p className="notice">No controls came back for this firm. Try again, or tell National IT if it persists.</p>
        ) : narrowed ? (
          <p className="notice">No controls match these filters.</p>
        ) : (
          <p className="ok">{filter === 'all' ? 'No controls match these filters.' : EMPTY[filter]}</p>
        )
      ) : narrowed ? (
        <ul className="plain work-list" aria-label="Controls">
          {shown.map(({ c, s }) => (
            <Row key={c.controlId} c={c} s={s} firmCode={d.firmCode} canUpdate={d.canUpdate} />
          ))}
        </ul>
      ) : (
        <>
          {(areaIds.length > 1 || needs.length > 0) && (
            <nav className="work-index small" aria-label="Areas">
              {needs.length > 0 && (
                <a href="#area-needs">
                  <strong>{d.canUpdate ? FIRST : 'Waiting on the firm'}</strong> <span className="muted">({needs.length})</span>
                </a>
              )}
              {areaIds.map((a) => (
                <a key={a} href={`#area-${a}`}>
                  {areaName(a)} <span className="muted">({rest.filter((r) => r.c.domainNumber === a).length})</span>
                </a>
              ))}
            </nav>
          )}
          {needs.length > 0 && (
            <section aria-labelledby="area-needs" className="work-area work-needs">
              <h2 id="area-needs">{d.canUpdate ? FIRST : 'Waiting on the firm'}</h2>
              <ul className="plain work-list">
                {needsShown.map(({ c, s }) => (
                  <Row key={c.controlId} c={c} s={s} firmCode={d.firmCode} canUpdate={d.canUpdate} />
                ))}
              </ul>
              {needsMore.length > 0 && (
                <details className="work-more">
                  <summary>
                    {needsMore.length === 1 ? 'Show 1 more rating not sent' : `Show ${needsMore.length} more ratings not sent`}
                  </summary>
                  <ul className="plain work-list">
                    {needsMore.map(({ c, s }) => (
                      <Row key={c.controlId} c={c} s={s} firmCode={d.firmCode} canUpdate={d.canUpdate} />
                    ))}
                  </ul>
                </details>
              )}
            </section>
          )}
          {areaIds.map((a) => (
            <section key={a} aria-labelledby={`area-${a}`} className="work-area">
              <h2 id={`area-${a}`}>{areaName(a)}</h2>
              <ul className="plain work-list">
                {rest
                  .filter((r) => r.c.domainNumber === a)
                  .map(({ c, s }) => (
                    <Row key={c.controlId} c={c} s={s} firmCode={d.firmCode} canUpdate={d.canUpdate} />
                  ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </>
  );
}

function Row({ c, s, firmCode, canUpdate }: { c: FirmControlRow; s: WorkState; firmCode: string; canUpdate: boolean }) {
  const name = CONTROL_SUMMARIES[c.controlId] ?? c.title;
  const to = `/firm/${firmCode}/controls/${c.controlId}`;
  const status = c.cdwStatus && (s === 'cdw' || s === 'draft') ? `CDW: ${resultLabel(c.cdwStatus)}` : null;
  // A CDW result wears the result's own colour, as in the comparison; other states their state's.
  const tone = s === 'cdw' && c.cdwStatus ? CDW_GAP_STATUS[c.cdwStatus]?.tone ?? 'neutral' : STATE[s].tone;
  return (
    <li className="work-row">
      {/* Two lines per row: the control, then its priority, state, reference and the one action. */}
      <Link to={to} className="work-name">
        {name}
      </Link>
      <div className="tags work-meta">
        <RiskChip controlId={c.controlId} />
        <span className={`chip tone-${tone}`}>{s === 'cdw' && status ? status : STATE[s].label}</span>
        {s === 'draft' && status && <span className="small muted">{status}</span>}
        {s === 'reviewed' && c.reviewedAt && <span className="small muted">on {formatDate(c.reviewedAt)}</span>}
        <span className="cid small muted">{c.framework === 'CIS' ? `CIS ${c.controlId}` : 'Essential Eight'}</span>
        {canUpdate && (
          <Link className="pill work-action" to={`${to}#assistant`} aria-label={`${STATE[s].action}: ${name}`}>
            <span className="work-action-long">{STATE[s].action}</span>
            <span className="work-action-short" aria-hidden="true">{STATE[s].short}</span>
          </Link>
        )}
      </div>
      {s === 'returned' && c.reviewNote && <p className="notice small">National IT asks: {c.reviewNote}</p>}
    </li>
  );
}
